import {
    FetchState,
    InvestmentThreshold,
    InvestmentCustodian,
    InvestmentProvider,
    FeatureFlags,
    InvestmentCustodianDisplayName,
} from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as drivewealthService from "@/shared/api/drivewealthService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useHsabInvestmentStore } from "@/shared/store/hsabInvestment";
import { useDrivewealthAccountMigrationStore } from "@/shared/store/drivewealthAccountMigration";
import { templatedFetchKeyStore } from "./fetchKeyedPlugin";
import { useDrivewealthStore } from "@/shared/store/drivewealth";

const investmentCustodianDisplayNames: InvestmentCustodianDisplayName[] = [
  ({key: InvestmentCustodian.Drivewealth, displayName: "DriveWealth"}),
  ({key: InvestmentCustodian.Tda, displayName: "TD Ameritrade"}),
  ({key: InvestmentCustodian.Umb, displayName: "UMB"}),
  ({key: InvestmentCustodian.None, displayName: "No Custodian"}),
];

const emptyThresholds = (): InvestmentThreshold => ({
    accountCreate: 0,
    moneyTransfer: 0,
    investmentProvider: InvestmentCustodian.None
});

const emptyProviders = (): InvestmentProvider => ({
    investmentProvider: InvestmentCustodian.None
});

const useSubStore = defineStore("investmentThreshold_fetchKeyed", () => {
    return templatedFetchKeyStore<InvestmentThreshold>(
        emptyThresholds,
        null,
        drivewealthService.getBalanceThresholds,
        undefined,
        true // retry on 404
    );
});

const useInvestmentProviderSubStore = defineStore("investmentProvider_fetchKeyed", () => {
    return templatedFetchKeyStore<InvestmentProvider>(
        emptyProviders,
        null,
        bankService.getInvestmentProvider,
        undefined,
        true // retry on 404
    );
});

export const useInvestmentThresholdStore = defineStore("investmentThreshold", {
    getters: {
        thresholdsLoaded (): boolean {
            return useSubStore().fetchState === FetchState.Loaded &&
                   useCustodianSettingsStore().isLoaded &&
                   useFeatureFlagStore().featureFlagsAvailable &&
                   !this.isWaitingOnHsabInvestmentService;
        },
        isDrivewealthInvestmentProvider (): boolean {
            return this.investmentProvider === InvestmentCustodian.Drivewealth;
        },
        isHsabInvestmentProvider (): boolean {
            return this.investmentProvider === InvestmentCustodian.HsaBank;
        },
        thresholdForAccountCreation (): number {
            const hsabInvestmentStore = useHsabInvestmentStore();
            const drivewealthAccountMigrationStore = useDrivewealthAccountMigrationStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            const subStore = useSubStore();
            const investmentProviderSubStore = useInvestmentProviderSubStore();
            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;
            const hasLegacyInvestmentAccount: boolean = hsabInvestmentStore.hasLegacyInvestmentAccount;


            const disableInvestmentThreshold: boolean = (
                investmentProviderSubStore.investmentProvider === InvestmentCustodian.Drivewealth &&
                isHsaBankCustodian &&
                drivewealthAccountMigrationStore.accountMigrationActive &&
                hasLegacyInvestmentAccount
            );

            if (disableInvestmentThreshold) {
                return 0;
            }

            return subStore.accountCreate;
        },
        thresholdForMoneyTransfer (): number {
            const subStore = useSubStore();

            return subStore.moneyTransfer;
        },
        investmentThreshold () {
            return (alreadyHasAccount?: boolean | any) => alreadyHasAccount
                ? this.thresholdForMoneyTransfer
                : this.thresholdForAccountCreation;
        },
        investmentProviderIsLoaded (): boolean {
            const investmentProviderSubStore = useInvestmentProviderSubStore();
            return investmentProviderSubStore.fetchState === FetchState.Loaded;
        },
        investmentProvider (): InvestmentCustodian {
            const investmentProviderSubStore = useInvestmentProviderSubStore();

            if (!(investmentProviderSubStore.fetchState === FetchState.Loaded &&
                useCustodianSettingsStore().isLoaded &&
                useFeatureFlagStore().featureFlagsAvailable)) {
                return InvestmentCustodian.None;
            }

            return investmentProviderSubStore.investmentProvider;
        },
        investmentProviderName (): string {
            const investmentProviderSubStore = useInvestmentProviderSubStore();

            const pair = investmentCustodianDisplayNames.find(x => x.key === investmentProviderSubStore.investmentProvider);
            if (pair) {
              return pair.displayName;
            }
            return `No display name for ${investmentProviderSubStore.investmentProvider}`;
        },
        displayInvestmentsSection (): boolean {
            const investmentProviderSubStore = useInvestmentProviderSubStore();
            const drivewealth = useDrivewealthStore();

            return investmentProviderSubStore.investmentProvider === InvestmentCustodian.Umb
                || (this.isDrivewealthInvestmentProvider && drivewealth.showInvestments);
        },
        displayLegacyHsabInvestmentsRedirectLink (): boolean {
            const hsabInvestmentStore = useHsabInvestmentStore();

            if (this.isHsabInvestmentProvider) {
                return hsabInvestmentStore.hsabInvestmentsEnabled;
            }

            return false;
        },
        displayMigrationPeriodHsabInvestmentsRedirectLink (): boolean {
            const custodianSettingsStore = useCustodianSettingsStore();
            const hsabInvestmentStore = useHsabInvestmentStore();

            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;

            return isHsaBankCustodian &&
                   this.isDrivewealthInvestmentProvider &&
                   hsabInvestmentStore.hsabInvestmentsEnabled;
        },
        isWaitingOnHsabInvestmentService (): boolean {
            const investmentProviderSubStore = useInvestmentProviderSubStore();

            return useCustodianSettingsStore().isHsaBankCustodian
                && (!useHsabInvestmentStore().investmentAccountStatusLoaded)
                && (investmentProviderSubStore.investmentProvider === InvestmentCustodian.HsaBank
                    // HSAB users with DW need to fetch WCF result due to
                    // the migration period
                    ||  investmentProviderSubStore.investmentProvider === InvestmentCustodian.Drivewealth);
        },
        shouldLoadHsabInvestmentAccountStatus (): boolean {
            const custodianSettingsStore = useCustodianSettingsStore();
            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;

            return isHsaBankCustodian && (this.isDrivewealthInvestmentProvider || this.isHsabInvestmentProvider);
        }
    },
    actions: {
        async loadInvestmentProvider (options?: { username?: string, partnerId?: number }): Promise<any> {
            const investmentProviderSubStore = useInvestmentProviderSubStore();
            return investmentProviderSubStore.fetchKeyedApiCall(options)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async loadInvestmentThresholds (options?: { username?: string, partnerId?: number }): Promise<any> {
            const username = options && options.username ? options.username : null;
            const partnerId = options && options.partnerId ? options.partnerId : null;

            const custodianStore = useCustodianSettingsStore();
            const custodianPromise = custodianStore.getCustodianSettings({ username, partnerId } as any);

            const subStore = useSubStore();
            const investmentProviderSubStore = useInvestmentProviderSubStore();

            return this.loadInvestmentProvider(options).
                then(() => {
                    if (this.investmentProviderIsLoaded) {
                        // If drivewealth is investment provider, get thresholds
                        // If not, set thresholds to 0 and do not call Drivewealth service
                        if (investmentProviderSubStore.investmentProvider === InvestmentCustodian.Drivewealth) {
                            subStore.fetchKeyedApiCall(options);
                        } else {
                            subStore.setLoading({
                                username: options && options.username ? options.username : null,
                                partnerId: options && options.partnerId ? options.partnerId : null,
                            });
                            subStore.setValueLoaded({
                                fetchKey: options,
                                data: {
                                    accountCreate: 0,
                                    moneyTransfer: 0,
                                    investmentProvider: investmentProviderSubStore.investmentProvider
                                }
                            });
                        }
                    }

                    return Promise.all([custodianPromise])
                        .then(() => {
                            if (this.shouldLoadHsabInvestmentAccountStatus) {
                                const hsabInvestmentStore = useHsabInvestmentStore();
                                return hsabInvestmentStore.loadInvestmentAccountStatus({ username, partnerId });
                            }
                        });

            }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});
